import {
  AlertDialog,
  AlertDialogBody,
  AlertDialogContent,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogOverlay,
  Box,
  Button,
  Flex,
  IconButton,
  Input,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Spinner,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tooltip,
  Tr,
  useDisclosure,
} from "@chakra-ui/react";
import { debounce } from "lodash";
import { useCallback, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { FaEdit, FaTrash } from "react-icons/fa";
import {
  ContactPersonDto,
  OrganizationEntryDto,
  OrganizationType,
  useGetOrganizationEntryQuery,
  useUpdateOrganizationEntryMutation,
} from "../../../../autogen/bff-api";
import { useLoggedInWithOrgContextState } from "../../../../common/auth/useLoggedInWithOrgContextState";
import { isCreator } from "../../../../common/auth/utils";
import { ContactPersonForm } from "../../../../common/ContactPersonForm";
import { isFetchBaseQueryError } from "../../../../common/errors/isFetchBaseQueryError";
import { useApiError } from "../../../../common/errors/useApiError";
import { displayPhoneNumber } from "../../../../common/formatting/displayPhoneNumber";
import { ModalSkeleton } from "../../../../common/ModalSkeleton";
import { OrganizationTypeTag } from "../../../../common/organization/OrganizationTypeTag";
import { InlineContactPersonEditForm } from "./InlineContactPersonEditForm";

interface Props {
  entryId: string;
  onClose: () => void;
  linkWithExistingOrg?: () => void;
}

export const OrganizationEntryModal = ({ entryId, onClose, linkWithExistingOrg }: Props) => {
  const [contactPersonToDelete, setContactPersonToDelete] = useState<ContactPersonDto | null>(null);
  const [contactPersonToEdit, setContactPersonToEdit] = useState<ContactPersonDto | null>(null);
  const { isOpen: isDeleteAlertOpen, onOpen: onDeleteAlertOpen, onClose: onDeleteAlertClose } = useDisclosure();
  const cancelRef = useRef<HTMLButtonElement>(null);
  const { t } = useTranslation();
  const authState = useLoggedInWithOrgContextState();
  const errorDisplayer = useApiError();

  const canUpdateOrgEntries = isCreator(authState.selectedOrg.roles);

  const { data: entry, isLoading } = useGetOrganizationEntryQuery({ entryId });

  const [updateOrgEntry, { isLoading: isUpdating }] = useUpdateOrganizationEntryMutation();

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const debouncedUpdateOrgEntry = useCallback(
    debounce(async (entry: OrganizationEntryDto) => {
      await updateOrgEntry({
        entryId: entry.id,
        updateOrganizationEntryRequest: {
          ...entry,
          countryId: entry.country.id,
        },
      })
        .unwrap()
        .catch((e) => {
          console.error(e);
          errorDisplayer.trigger(e);
        });
    }, 300),
    []
  );

  return (
    <Modal isOpen={true} onClose={onClose} size="5xl">
      <ModalOverlay />
      <ModalContent maxHeight="80%" overflowY="auto" pb="2.5">
        <ModalHeader>
          {entry && (
            <>
              {canUpdateOrgEntries ? (
                <Flex align="center">
                  <Input
                    defaultValue={entry.name}
                    onChange={(e) => {
                      const name = e.target.value;
                      if (name) debouncedUpdateOrgEntry({ ...entry, name });
                    }}
                  />
                  {isUpdating && <Spinner color="smMuted" ml="-8" />}
                </Flex>
              ) : (
                entry.name
              )}
            </>
          )}
          {!entry && isLoading && `${t("Loading")}...`}
          <Box mt="5px">
            <OrganizationTypeTag type={OrganizationType.OrganizationEntry} />
          </Box>
        </ModalHeader>
        <ModalBody>
          {entry && (
            <Flex flexDirection={"column"} rounded="lg">
              <Text fontWeight="bold">{t("Country")}</Text>
              <Text>{entry.country.name}</Text>
              <Text fontWeight="bold" pt="5">
                {t("Organization number")}
              </Text>
              {canUpdateOrgEntries ? (
                <Flex align="center" pt="1">
                  <Input
                    backgroundColor="smBackground"
                    defaultValue={entry.organizationNumber}
                    onChange={(e) => {
                      const organizationNumber = e.target.value;
                      if (organizationNumber) debouncedUpdateOrgEntry({ ...entry, organizationNumber });
                    }}
                  />
                  {isUpdating && <Spinner color="smMuted" ml="-8" />}
                </Flex>
              ) : (
                <Text>{entry.name}</Text>
              )}
              <Text fontSize="md" fontWeight="bold" pt="5" pb="2">
                {t("Contact persons")}
              </Text>
              {entry.contactPersons?.length ? (
                <Flex
                  width="full"
                  maxWidth="full"
                  overflowX="auto"
                  border="1px solid"
                  borderColor="smBorder"
                  rounded="lg"
                  pt="5"
                  pb="2.5"
                >
                  <Table>
                    <Thead>
                      <Tr>
                        <Th>{t("Name")}</Th>
                        <Th>{t("Title")}</Th>
                        <Th>{t("Email")}</Th>
                        <Th>{t("Phone number")}</Th>
                        {canUpdateOrgEntries && <Th width="100px">{t("Actions")}</Th>}
                      </Tr>
                    </Thead>
                    <Tbody>
                      {entry.contactPersons.map((p) =>
                        contactPersonToEdit && contactPersonToEdit.email === p.email ? (
                          <InlineContactPersonEditForm
                            key={p.email}
                            initialContactPerson={contactPersonToEdit}
                            onSave={(updatedContactPerson) => {
                              updateOrgEntry({
                                entryId: entry.id,
                                updateOrganizationEntryRequest: {
                                  editContactPerson: updatedContactPerson,
                                },
                              })
                                .unwrap()
                                .then(() => {
                                  setContactPersonToEdit(null);
                                })
                                .catch((e) => {
                                  console.error(e);
                                  errorDisplayer.trigger(e);
                                });
                            }}
                            onCancel={() => {
                              setContactPersonToEdit(null);
                            }}
                            canUpdateOrgEntries={canUpdateOrgEntries}
                          />
                        ) : (
                          <Tr key={p.email}>
                            <Td>{p.name}</Td>
                            <Td>{p.title}</Td>
                            <Td>{p.email}</Td>
                            <Td>{p.phoneNumber ? displayPhoneNumber(p.phoneNumber) : ""}</Td>
                            {canUpdateOrgEntries && (
                              <Td>
                                <Flex gap={2}>
                                  <Tooltip
                                    label={
                                      contactPersonToEdit
                                        ? t("Finish editing current contact person")
                                        : t("Edit contact person")
                                    }
                                  >
                                    <IconButton
                                      aria-label={t("Edit contact person")}
                                      icon={<FaEdit />}
                                      size="sm"
                                      colorScheme="blue"
                                      variant="ghost"
                                      isDisabled={contactPersonToEdit !== null}
                                      onClick={() => {
                                        setContactPersonToEdit(JSON.parse(JSON.stringify(p))); // Deep copy
                                      }}
                                    />
                                  </Tooltip>
                                  <Tooltip
                                    label={
                                      contactPersonToEdit
                                        ? t("Finish editing current contact person")
                                        : t("Delete contact person")
                                    }
                                  >
                                    <IconButton
                                      aria-label={t("Delete contact person")}
                                      icon={<FaTrash />}
                                      size="sm"
                                      colorScheme="red"
                                      variant="ghost"
                                      isDisabled={contactPersonToEdit !== null}
                                      onClick={() => {
                                        setContactPersonToDelete(p);
                                        onDeleteAlertOpen();
                                      }}
                                    />
                                  </Tooltip>
                                </Flex>
                              </Td>
                            )}
                          </Tr>
                        )
                      )}
                    </Tbody>
                  </Table>
                </Flex>
              ) : (
                <Text as="span" fontSize={"sm"} color="smMuted">
                  <i>{t("Not provided")}.</i>
                </Text>
              )}
              {canUpdateOrgEntries && entry && !contactPersonToEdit && (
                <ContactPersonForm
                  organizationEntry={entry}
                  onSubmit={async (newContactPerson) => {
                    try {
                      await updateOrgEntry({
                        entryId: entry.id,
                        updateOrganizationEntryRequest: {
                          addContactPerson: newContactPerson,
                          countryId: entry.country.id,
                        },
                      }).unwrap();
                    } catch (e) {
                      console.error(e);
                      if (isFetchBaseQueryError(e)) errorDisplayer.trigger(e);
                    }
                  }}
                />
              )}
              <AlertDialog isOpen={isDeleteAlertOpen} leastDestructiveRef={cancelRef} onClose={onDeleteAlertClose}>
                <AlertDialogOverlay>
                  <AlertDialogContent>
                    <AlertDialogHeader fontSize="lg" fontWeight="bold">
                      {t("Delete Contact Person")}
                    </AlertDialogHeader>

                    <AlertDialogBody>
                      {`${t("Are you sure you want to delete this contact person")}? ${t(
                        "This action cannot be undone"
                      )}.`}
                    </AlertDialogBody>

                    <AlertDialogFooter>
                      <Button ref={cancelRef} onClick={onDeleteAlertClose}>
                        {t("Cancel")}
                      </Button>
                      <Button
                        colorScheme="red"
                        onClick={() => {
                          if (contactPersonToDelete && entry) {
                            updateOrgEntry({
                              entryId: entry.id,
                              updateOrganizationEntryRequest: {
                                removeContactPersonEmail: contactPersonToDelete.email,
                                countryId: entry.country.id,
                              },
                            })
                              .unwrap()
                              .then(() => {
                                setContactPersonToDelete(null);
                                onDeleteAlertClose();
                              })
                              .catch((e) => {
                                console.error(e);
                                errorDisplayer.trigger(e);
                              });
                          }
                        }}
                        ml={3}
                      >
                        {t("Delete")}
                      </Button>
                    </AlertDialogFooter>
                  </AlertDialogContent>
                </AlertDialogOverlay>
              </AlertDialog>
            </Flex>
          )}
          {!entry && isLoading && <ModalSkeleton />}
        </ModalBody>
        {!entry?.link && linkWithExistingOrg && canUpdateOrgEntries && (
          <ModalFooter mt="-10px">
            <Button variant="solid" colorScheme={"purple"} onClick={linkWithExistingOrg}>
              {t("Link with existing org")}
            </Button>
          </ModalFooter>
        )}
      </ModalContent>
    </Modal>
  );
};
